import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ComplianceBadge } from "@/components/ComplianceBadge";

export default function Orders() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending_manual_review");
  const [open, setOpen] = useState(null);
  const [notes, setNotes] = useState("");
  const [ship, setShip] = useState({ tracking_number: "", carrier: "usps", note: "" });

  const load = () => api.get("/admin/orders", { params: filter ? { status: filter } : {} }).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => {
    if (open?.shipping) {
      setShip({
        tracking_number: open.shipping.tracking_number || "",
        carrier: open.shipping.carrier || "usps",
        note: open.shipping.note || "",
      });
    } else {
      setShip({ tracking_number: "", carrier: "usps", note: "" });
    }
  }, [open]);

  const decide = async (decision) => {
    if (!open) return;
    await api.post(`/admin/orders/${open.id}/decision`, { decision, notes });
    toast.success(decision === "approve" ? "Approved" : "Rejected");
    setOpen(null); setNotes(""); load();
  };

  const markShipped = async () => {
    if (!open) return;
    if (!ship.tracking_number || ship.tracking_number.length < 6) {
      toast.error("Enter a tracking number (6+ characters)."); return;
    }
    try {
      await api.post(`/admin/orders/${open.id}/ship`, ship);
      toast.success("Order marked as shipped — buyer notified");
      setOpen(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ship action failed");
    }
  };

  const resendTracking = async () => {
    if (!open) return;
    try {
      await api.post(`/admin/orders/${open.id}/resend-tracking`);
      toast.success("Tracking email re-sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-send failed");
    }
  };

  const verifyCrypto = async (decision) => {
    if (!open) return;
    try {
      await api.post(`/admin/orders/${open.id}/verify-crypto`, { decision, notes });
      toast.success(decision === "approve" ? "Crypto confirmed · order approved" : "Tx rejected · returned to awaiting payment");
      setOpen(null); setNotes(""); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-10">
      <div className="label-eyebrow">Compliance</div>
      <h1 className="text-2xl font-bold tracking-tight mt-1">Orders & Manual Review</h1>

      <div className="mt-6 flex items-center gap-2 flex-wrap">
        {[
          ["pending_manual_review", "Pending"],
          ["pending_crypto_verification", "Crypto verify"],
          ["awaiting_payment", "Awaiting payment"],
          ["approved", "Approved · ready to ship"],
          ["shipped", "Shipped"],
          ["rejected", "Rejected"],
          ["", "All"],
        ].map(([v, l]) => (
          <button key={l} onClick={() => setFilter(v)} className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border ${filter === v ? "border-[#003366] bg-[#003366] text-white" : "border-[#D1D5DB]"}`} data-testid={`order-filter-${v || "all"}`}>{l}</button>
        ))}
      </div>

      <div className="mt-6 border border-[#D1D5DB] overflow-x-auto">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2">Order #</th>
              <th className="text-left px-4 py-2">Buyer</th>
              <th className="text-left px-4 py-2">Type</th>
              <th className="text-left px-4 py-2">Items</th>
              <th className="text-left px-4 py-2">Total</th>
              <th className="text-left px-4 py-2">Triggers</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center font-mono text-xs text-[#5E636E]">No orders.</td></tr>}
            {items.map((o) => (
              <tr key={o.id} data-testid={`admin-order-${o.order_number}`}>
                <td className="px-4 py-3 font-mono">{o.order_number}</td>
                <td className="px-4 py-3 text-xs">{o.user_email}</td>
                <td className="px-4 py-3 text-xs">{o.buyer_type}</td>
                <td className="px-4 py-3 font-mono text-xs">{o.items?.length || 0}</td>
                <td className="px-4 py-3 font-mono">${(o.subtotal_usd || 0).toFixed(2)}</td>
                <td className="px-4 py-3 text-xs">
                  {(o.review_triggers || []).map((t) => <span key={t} className="inline-block bg-[#FFF7E6] text-[#9A6B00] border border-[#F5A623] px-1.5 py-0.5 mr-1 mb-1 font-mono text-[10px]">{t}</span>)}
                </td>
                <td className="px-4 py-3"><ComplianceBadge kind="order_status" value={o.status} /></td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setOpen(o)} className="text-xs font-mono uppercase tracking-wider text-[#003366] hover:underline" data-testid={`open-order-${o.order_number}`}>Review</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={() => setOpen(null)}>
          <div className="w-full max-w-2xl h-full bg-white border-l border-[#D1D5DB] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-[#D1D5DB]">
              <div className="label-eyebrow">Manual Review</div>
              <div className="font-mono text-lg font-semibold mt-1">{open.order_number}</div>
              <ComplianceBadge kind="order_status" value={open.status} />
            </div>
            <div className="px-6 py-4 space-y-5 text-sm">
              <Section title="Buyer">
                <div className="font-mono text-xs">{open.user_email}</div>
                <div className="font-mono text-xs text-[#5E636E]">{open.buyer_type}</div>
              </Section>
              <Section title="Items">
                <table className="w-full data-table">
                  <thead><tr><th className="text-left px-2 py-1">SKU</th><th className="text-left px-2 py-1">Item</th><th className="text-left px-2 py-1">Qty</th><th className="text-right px-2 py-1">Total</th></tr></thead>
                  <tbody>
                    {open.items.map((i) => (
                      <tr key={i.sku}><td className="px-2 py-1 font-mono">{i.sku}</td><td className="px-2 py-1">{i.name}</td><td className="px-2 py-1 font-mono">{i.quantity}</td><td className="px-2 py-1 font-mono text-right">${(i.line_total).toFixed(2)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </Section>
              <Section title="Shipping">
                <pre className="text-xs font-mono whitespace-pre-wrap bg-[#F7F7F8] border border-[#D1D5DB] p-3">{JSON.stringify(open.shipping_address, null, 2)}</pre>
              </Section>
              <Section title="Attestations">
                <pre className="text-xs font-mono whitespace-pre-wrap bg-[#F7F7F8] border border-[#D1D5DB] p-3">{JSON.stringify(open.attestations, null, 2)}</pre>
                <div className="text-xs font-mono mt-2">Terms version: {open.terms_version}</div>
              </Section>
              <Section title="Review triggers">
                {(open.review_triggers || []).map((t) => <span key={t} className="inline-block bg-[#FFF7E6] text-[#9A6B00] border border-[#F5A623] px-2 py-0.5 mr-1 mb-1 font-mono text-[11px]">{t}</span>)}
              </Section>
              {open.card_payment && (
                <Section title="Card payment · VERIFIED Crypto Checkout">
                  <div className="text-xs font-mono space-y-1 bg-[#F7F7F8] border border-[#D1D5DB] p-3" data-testid="card-payment-info">
                    <div>Status: <strong className={open.card_payment.status === "paid" ? "text-[#0B6623]" : open.card_payment.status === "failed" ? "text-[#A30414]" : ""}>{open.card_payment.status}</strong></div>
                    <div>Amount: <strong>${Number(open.card_payment.amount_usd || 0).toFixed(2)}</strong></div>
                    <div>Session: <span className="break-all">{open.card_payment.session_id || "—"}</span></div>
                    <div>Transaction: <span className="break-all">{open.card_payment.transaction_id || "—"}</span></div>
                    <div>Paid at: {open.card_payment.paid_at?.slice(0, 19).replace("T", " ") || "—"}</div>
                    {open.card_payment.failure_reason && <div>Failure: {open.card_payment.failure_reason}</div>}
                  </div>
                </Section>
              )}
              {open.status === "pending_manual_review" && (
                <Section title="Decision">
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Reviewer notes" rows={3} className="w-full border border-[#D1D5DB] bg-white p-2 text-sm" data-testid="review-notes" />
                  <div className="mt-3 flex gap-3">
                    <button onClick={() => decide("approve")} className="btn-primary" style={{ background: "#0B6623" }} data-testid="approve-order">Approve order</button>
                    <button onClick={() => decide("reject")} className="btn-primary" style={{ background: "#A30414" }} data-testid="reject-order">Reject order</button>
                  </div>
                </Section>
              )}
              {open.status === "pending_crypto_verification" && (
                <Section title="Crypto verification">
                  <div className="text-xs font-mono space-y-1 bg-[#F7F7F8] border border-[#D1D5DB] p-3">
                    <div>Currency: <strong>{open.crypto_payment?.currency?.toUpperCase()}</strong></div>
                    <div>Sent amount: <strong>{open.crypto_payment?.sent_amount ?? "—"}</strong></div>
                    <div>Address: <span className="break-all">{open.crypto_quote?.address}</span></div>
                    <div>Tx hash: <span className="break-all">{open.crypto_payment?.tx_hash}</span></div>
                    <div>Submitted: {open.crypto_payment?.submitted_at?.slice(0, 19).replace("T", " ")}</div>
                  </div>
                  {open.crypto_payment?.tx_hash && (
                    <a
                      href={(open.crypto_payment.currency === "btc"
                        ? "https://mempool.space/tx/"
                        : "https://solscan.io/tx/") + open.crypto_payment.tx_hash}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-2 text-xs font-mono text-[#003366] hover:underline"
                      data-testid="admin-view-tx"
                    >Open on block explorer ↗</a>
                  )}
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Reviewer notes (optional)" rows={2} className="w-full border border-[#D1D5DB] bg-white p-2 text-sm mt-3" data-testid="crypto-notes" />
                  <div className="mt-3 flex gap-3">
                    <button onClick={() => verifyCrypto("approve")} className="btn-primary" style={{ background: "#0B6623" }} data-testid="approve-crypto">Confirm payment · Approve</button>
                    <button onClick={() => verifyCrypto("reject")} className="btn-primary" style={{ background: "#A30414" }} data-testid="reject-crypto">Reject tx</button>
                  </div>
                </Section>
              )}

              {open.review_notes && (
                <Section title="Reviewer notes">
                  <div className="text-sm">{open.review_notes}</div>
                  <div className="text-xs font-mono text-[#5E636E] mt-1">By {open.reviewed_by} · {open.reviewed_at?.slice(0, 19).replace("T", " ")}</div>
                </Section>
              )}

              {(open.status === "approved" || open.status === "shipped") && (
                <Section title={open.status === "shipped" ? "Shipping · already shipped" : "Ship order"}>
                  {open.status === "shipped" && open.shipping && (
                    <div className="bg-[#E8F5EC] border border-[#0B6623] rounded p-3 text-xs font-mono mb-3" data-testid="ship-current">
                      <div><strong>{open.shipping.carrier_label || open.shipping.carrier?.toUpperCase()}</strong> · {open.shipping.tracking_number}</div>
                      {open.shipping.tracking_url && (
                        <a href={open.shipping.tracking_url} target="_blank" rel="noopener noreferrer" className="text-[#003366] hover:underline break-all" data-testid="ship-track-link">
                          {open.shipping.tracking_url}
                        </a>
                      )}
                      <div className="text-[#5E636E] mt-1">Shipped {open.shipping.shipped_at?.slice(0, 19).replace("T", " ")} by {open.shipping.shipped_by}</div>
                      {open.shipping.note && <div className="text-[#5E636E] mt-1 italic">"{open.shipping.note}"</div>}
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <select
                      value={ship.carrier}
                      onChange={(e) => setShip({ ...ship, carrier: e.target.value })}
                      className="border border-[#D1D5DB] bg-white p-2 text-sm rounded"
                      data-testid="ship-carrier"
                    >
                      <option value="usps">USPS</option>
                      <option value="ups">UPS</option>
                      <option value="fedex">FedEx</option>
                      <option value="dhl">DHL</option>
                      <option value="other">Other</option>
                    </select>
                    <input
                      value={ship.tracking_number}
                      onChange={(e) => setShip({ ...ship, tracking_number: e.target.value.trim() })}
                      placeholder="Tracking number"
                      className="col-span-2 border border-[#D1D5DB] bg-white p-2 text-sm font-mono rounded"
                      data-testid="ship-tracking-input"
                    />
                  </div>
                  <input
                    value={ship.note}
                    onChange={(e) => setShip({ ...ship, note: e.target.value })}
                    placeholder="Optional note to buyer (e.g. 'Ships with cold pack')"
                    className="w-full border border-[#D1D5DB] bg-white p-2 text-sm rounded"
                    data-testid="ship-note"
                  />
                  <div className="mt-3 flex gap-3">
                    <button
                      onClick={markShipped}
                      className="btn-primary"
                      style={{ background: open.status === "shipped" ? "#003366" : "#0B6623" }}
                      data-testid="ship-mark-btn"
                    >
                      {open.status === "shipped" ? "Update tracking · re-notify" : "Mark shipped · notify buyer"}
                    </button>
                    {open.status === "shipped" && (
                      <button
                        onClick={resendTracking}
                        className="btn-primary"
                        style={{ background: "#5E636E" }}
                        data-testid="ship-resend-btn"
                      >
                        Re-send tracking email
                      </button>
                    )}
                  </div>
                </Section>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <div className="label-eyebrow mb-2">{title}</div>
    {children}
  </div>
);
